<template>
  <v-menu
    v-model="modal"
    :close-on-content-click="false"
    transition="scale-transition"
    min-width="290px"
  >
    <template #activator="{ props }">
      <v-text-field
        :model-value="shown"
        :error-messages="errorMessages"
        :rules="rules"
        :label="label"
        readonly
        v-bind="props"
        :density="dense ? 'compact' : undefined"
        :variant="outlined ? 'outlined' : undefined"
        :disabled="disabled"
      />
    </template>
    <!-- A month, not a day: Vuetify 3's date picker has no month-only mode, so
         this is its month grid under a year you can step through — what
         Vuetify 2's type="month" drew. -->
    <v-card v-if="type === 'month'" class="datepicker-month">
      <div class="d-flex align-center pa-2">
        <v-btn icon="mdi-chevron-left" variant="text" size="small" @click="year--" />
        <v-spacer />
        <span class="text-subtitle-1 font-weight-medium">{{ year }}</span>
        <v-spacer />
        <v-btn icon="mdi-chevron-right" variant="text" size="small" @click="year++" />
      </div>
      <!-- Not v-date-picker-months: opened with no month it picks the current
           one by itself and reports that as the reader's choice. -->
      <div class="datepicker-month__grid">
        <v-btn
          v-for="(name, index) in MONTHS"
          :key="name"
          :variant="index === month ? 'flat' : 'text'"
          :color="index === month ? 'primary' : undefined"
          rounded="xl"
          size="small"
          @click="pickMonth(index)"
        >{{ name }}</v-btn>
      </div>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" color="primary" @click="modal = false">OK</v-btn>
      </v-card-actions>
    </v-card>
    <!-- The title bar is what carries the year as a button: a date like when
         a boiler was installed is usually years back, and flipping there a
         month at a time is no way to enter it. One click on the year opens
         the year list, then the months, then the days. -->
    <v-date-picker
      v-else
      :model-value="pickerDate"
      color="primary"
      show-adjacent-months
      @update:model-value="pickDay"
    >
      <template #actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" color="primary" @click="modal = false">OK</v-btn>
      </template>
    </v-date-picker>
  </v-menu>
</template>

<script>
import dayjs from 'dayjs'

const MONTHS = ['jaan', 'veebr', 'märts', 'apr', 'mai', 'juuni', 'juuli', 'aug', 'sept', 'okt', 'nov', 'dets']

// The value is a string both ways, as it was in Vuetify 2: 'YYYY-MM-DD', or
// 'YYYY-MM' for type="month". Vuetify 3's pickers work in Date objects and
// month indexes; the conversion stays in here.
export default {
  emits: ['update:modelValue'],
  data () {
    return {
      date: undefined,
      modal: false,
      MONTHS,
      year: dayjs().year()
    }
  },
  props: [
    'modelValue',
    'label',
    'error-messages',
    'rules',
    'type',
    'dense',
    'outlined',
    'disabled'
  ],
  computed: {
    // As Estonia writes it; the value itself stays YYYY-MM-DD / YYYY-MM.
    shown () {
      if (!this.date) return this.date
      return this.type === 'month'
        ? dayjs(this.date + '-01').format('MM.YYYY')
        : dayjs(this.date).format('DD.MM.YYYY')
    },
    pickerDate () {
      return this.date ? dayjs(this.date, 'YYYY-MM-DD').toDate() : undefined
    },
    // The month grid marks the picked month only while it shows that year.
    month () {
      if (!this.date) {
        return undefined
      }
      const picked = dayjs(this.date, 'YYYY-MM')
      return picked.year() === this.year ? picked.month() : undefined
    }
  },
  watch: {
    date (value) {
      if (value !== this.modelValue) {
        this.$emit('update:modelValue', value)
      }
    },
    modelValue (value) {
      this.date = value
    },
    modal (open) {
      if (open && this.type === 'month' && this.date) {
        this.year = dayjs(this.date, 'YYYY-MM').year()
      }
    }
  },
  methods: {
    pickDay (value) {
      this.date = value ? dayjs(value).format('YYYY-MM-DD') : value
    },
    pickMonth (month) {
      this.date = dayjs().year(this.year).month(month).format('YYYY-MM')
    }
  },
  mounted () {
    this.date = this.modelValue
  }
}
</script>

<style scoped>
.datepicker-month__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; padding: 4px 12px 8px; }
</style>
